/**
 * SEARCH-E4：Journeys discovery panel — draft／applied query、URL 同步、suggestion 選取、race-safe load。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { journeyDiscoveryApi } from '../../api/journeyDiscoveryApi';
import { discoveryDestinationApi } from '../../api/discoveryDestinationApi';
import JourneyDestinationPicker from './JourneyDestinationPicker';
import { discoveryLocationApi } from '../../api/discoveryLocationApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { snowflakePositiveId } from '../social-compose/socialComposeModel';
import SocialLocationSelector from '../social-compose/SocialLocationSelector';
import { journeySearchNextCursor, normalizeJourneySearchPage, JOURNEY_DISCOVERY_SORTS } from './discoverModel';
import JourneyDiscoveryCard from './JourneyDiscoveryCard';
import ExploreGuestGate from './ExploreGuestGate';
import JourneyFilterDialog from './JourneyFilterDialog';
import JourneySearchCombobox from './JourneySearchCombobox';
import useDiscoverySuggestions from './useDiscoverySuggestions';
import { readJourneyReturnState, saveJourneyReturnState } from './journeyDiscoveryReturnState';
import {
  filtersEqual,
  readJourneySearchFromParams,
  writeJourneySearchToParams,
} from './journeySearchUrl';

const DEFAULT_LIMIT = 20;

function parsePositiveInt(value) {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) && n > 0 ? n : '';
}

function emptyFilters(overrides = {}) {
  return {
    q: '',
    titleQuery: '',
    scope: 'ALL',
    locationId: '',
    destinationKey: '',
    budgetMin: '',
    budgetMax: '',
    currency: '',
    minDays: '',
    maxDays: '',
    sort: 'LATEST',
    ...overrides,
  };
}

export default function JourneysPanel({ dedicated = false, canBrowseFollowing = true, hasFollowing = null, returnStateKey: returnStateIdentity = null } = {}) {
  const { t, tf } = useTravelTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = readJourneySearchFromParams(searchParams);
  const suggestions = useDiscoverySuggestions({ initialQuery: urlFilters.q });

  const [pendingTitle, setPendingTitle] = useState(urlFilters.titleQuery);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pendingDestinationKey, setPendingDestinationKey] = useState(urlFilters.destinationKey);
  const [pendingLocationId, setPendingLocationId] = useState(urlFilters.locationId);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [pendingBudgetMin, setPendingBudgetMin] = useState(urlFilters.budgetMin);
  const [pendingBudgetMax, setPendingBudgetMax] = useState(urlFilters.budgetMax);
  const [pendingCurrency, setPendingCurrency] = useState(urlFilters.currency);
  const [pendingMinDays, setPendingMinDays] = useState(urlFilters.minDays);
  const [pendingMaxDays, setPendingMaxDays] = useState(urlFilters.maxDays);
  const [pendingSort, setPendingSort] = useState(urlFilters.sort);

  const [appliedFilters, setAppliedFilters] = useState(() => emptyFilters(urlFilters));
  const followingBlocked = dedicated && appliedFilters.scope === 'FOLLOWING' && !canBrowseFollowing;
  const returnStateKey = returnStateIdentity ? `${returnStateIdentity}:${JSON.stringify(appliedFilters)}` : null;

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const generation = useRef(0);
  const abortRef = useRef(null);
  const skipNextUrlSync = useRef(false);
  const minDaysRef = useRef(null);
  const hydratedLocationRef = useRef('');
  const hydratedDestinationRef = useRef('');
  const selectionRevision = useRef(0);
  const firstUrlWrite = useRef(true);
  const returnView = useRef(null);
  useEffect(() => {
    returnView.current = { key: returnStateKey, count: items.length };
  }, [returnStateKey, items.length]);
  useEffect(() => () => {
    const view = returnView.current;
    if (view?.key === returnStateKey) saveJourneyReturnState(returnStateKey, view.count, window.scrollY);
  }, [returnStateKey]);


  // Back／Forward：URL 變更時還原 applied／pending／draft
  useEffect(() => {
    const next = readJourneySearchFromParams(searchParams);
    if (filtersEqual(next, appliedFilters)) return;
    skipNextUrlSync.current = true;
    setAppliedFilters(emptyFilters(next));
    setPendingTitle(next.titleQuery);
    setPendingLocationId(next.locationId);
    setPendingDestinationKey(next.destinationKey);
    setPendingBudgetMin(next.budgetMin);
    setPendingBudgetMax(next.budgetMax);
    setPendingCurrency(next.currency);
    setPendingMinDays(next.minDays);
    setPendingMaxDays(next.maxDays);
    setPendingSort(next.sort);
    suggestions.syncDraftQuery(next.q, { fetch: false });
    if (!next.locationId && !next.destinationKey) setPendingLocation(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // applied → URL：首次 normalize 用 replace；之後使用者搜尋／filter 用 push 以支援 Back／Forward
  useEffect(() => {
    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }
    const nextParams = writeJourneySearchToParams(searchParams, appliedFilters);
    if (dedicated) nextParams.delete('tab');
    else nextParams.set('tab', 'journeys');
    const replace = firstUrlWrite.current;
    firstUrlWrite.current = false;
    if (nextParams.toString() === searchParams.toString()) return;
    setSearchParams(nextParams, { replace });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, followingBlocked]);

  // 還原 location label（僅 id 在 URL 時）
  useEffect(() => {
    const id = snowflakePositiveId(appliedFilters.locationId);
    if (!id) {
      hydratedLocationRef.current = '';
      return;
    }
    if (hydratedLocationRef.current === id) return;
    let cancelled = false;
    const revision = selectionRevision.current;
    const request = discoveryLocationApi.getById?.(id);
    if (!request || typeof request.then !== 'function') {
      hydratedLocationRef.current = id;
      return undefined;
    }
    request.then((payload) => {
      if (cancelled || revision !== selectionRevision.current) return;
      const node = payload?.locationId ? payload : payload?.data;
      if (node?.locationId) {
        hydratedLocationRef.current = String(node.locationId);
        setPendingLocation({
          locationId: String(node.locationId),
          displayName: node.displayName || node.canonicalName || String(node.locationId),
          hasChildren: Boolean(node.hasChildren),
        });
        setPendingLocationId(String(node.locationId));
      }
    }).catch(() => {
      if (cancelled || revision !== selectionRevision.current) return;
      hydratedLocationRef.current = id;
      setPendingLocation({
        locationId: id,
        displayName: t('travel.discover.filters.selectedLocation') || 'Selected location',
        hasChildren: false,
      });
    });
    return () => { cancelled = true; };
  }, [appliedFilters.locationId, t]);

  useEffect(() => {
    const key = appliedFilters.destinationKey;
    if (!key) { hydratedDestinationRef.current = ''; return undefined; }
    if (hydratedDestinationRef.current === key) return undefined;
    const revision = selectionRevision.current;
    const controller = new AbortController();
    discoveryDestinationApi.resolve(key, { signal: controller.signal }).then((raw) => {
      if (controller.signal.aborted || revision !== selectionRevision.current) return;
      const node = raw?.data ?? raw;
      hydratedDestinationRef.current = key;
      setPendingLocation({ destinationKey: key, displayName: node.displayName || node.canonicalName, hasChildren: node.hasChildren });
    }).catch(() => { /* Search reports an unavailable scope; do not replace it with an unfiltered query. */ });
    return () => controller.abort();
  }, [appliedFilters.destinationKey]);

  const load = useCallback(async ({ append = false, cursor = null, filters = appliedFilters } = {}) => {
    const requestGeneration = ++generation.current;
    if (followingBlocked) { setPhase('idle'); return; }
    if (!append && abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const params = {
        sort: filters.sort || 'LATEST',
        limit: DEFAULT_LIMIT,
      };
      if (filters.q) params.q = filters.q;
      if (filters.titleQuery) params.titleQuery = filters.titleQuery;
      if (filters.scope === 'FOLLOWING') params.scope = filters.scope;
      if (filters.destinationKey) params.destinationKey = filters.destinationKey;
      if (filters.locationId) params.locationId = filters.locationId;
      if (filters.budgetMin !== '') params.budgetMin = filters.budgetMin;
      if (filters.budgetMax !== '') params.budgetMax = filters.budgetMax;
      if (filters.currency) params.currency = filters.currency;
      if (filters.minDays !== '') params.minDays = filters.minDays;
      if (filters.maxDays !== '') params.maxDays = filters.maxDays;
      if (cursor) Object.assign(params, cursor);

      const payload = await journeyDiscoveryApi.searchJourneys(params, { signal: controller.signal });
      if (requestGeneration !== generation.current) return;

      const page = normalizeJourneySearchPage(payload, filters.sort);
      setItems((current) => (append ? [...current, ...page.items] : page.items));
      setNextCursor(journeySearchNextCursor(payload, filters.sort));
      setHasMore(page.hasMore);
      setPhase('ready');
      return { ...page, nextCursor: journeySearchNextCursor(payload, filters.sort) };
    } catch (cause) {
      if (cause?.name === 'CanceledError' || cause?.name === 'AbortError' || cause?.code === 'ERR_CANCELED') {
        return;
      }
      if (requestGeneration !== generation.current) return;
      setError(cause?.message || t('travel.discover.error.loadFailed') || 'Could not load journeys.');
      setPhase('error');
    }
  }, [appliedFilters, t, followingBlocked]);

  useEffect(() => {
    let cancelled = false;
    let restoreFrame = null;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setError('');
    const previous = readJourneyReturnState(returnStateKey);
    // Re-fetch all previously visible pages before restoring scroll, so removed/private
    // publications never reappear from a client-side content cache.
    async function restore() {
      let result = await load({ filters: appliedFilters });
      let count = result?.items?.length || 0;
      const visitedCursors = new Set();
      while (!cancelled && previous && count < previous.count && result?.hasMore && result.nextCursor) {
        const cursorKey = JSON.stringify(result.nextCursor);
        if (visitedCursors.has(cursorKey)) break;
        visitedCursors.add(cursorKey);
        result = await load({ append: true, cursor: result.nextCursor, filters: appliedFilters });
        count += result?.items?.length || 0;
      }
      if (!cancelled && returnStateKey) {
        restoreFrame = requestAnimationFrame(() => {
          if (!cancelled) window.scrollTo(0, previous?.scrollY || 0);
        });
      }
    }
    restore();
    return () => {
      cancelled = true;
      if (restoreFrame != null) cancelAnimationFrame(restoreFrame);
      if (abortRef.current) abortRef.current.abort();
      generation.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, followingBlocked, returnStateKey]);

  function commitFilters(next) {
    const normalized = emptyFilters({
      ...next,
      currency: (next.budgetMin !== '' || next.budgetMax !== '') ? (next.currency || '') : '',
    });
    setAppliedFilters(normalized);
  }

  function applyFilters() {
    commitFilters({
      q: dedicated && pendingTitle.trim() ? '' : appliedFilters.q,
      titleQuery: pendingTitle.trim(),
      scope: appliedFilters.scope,
      locationId: pendingDestinationKey ? '' : pendingLocationId,
      destinationKey: pendingDestinationKey,
      budgetMin: pendingBudgetMin,
      budgetMax: pendingBudgetMax,
      currency: pendingCurrency,
      minDays: pendingMinDays,
      maxDays: pendingMaxDays,
      sort: pendingSort,
    });
    setFiltersOpen(false);
  }

  function applySort(sort) {
    setPendingSort(sort);
    commitFilters({ ...appliedFilters, sort });
  }

  function clearPendingFilters() {
    selectionRevision.current += 1; setPendingDestinationKey('');
    setPendingLocationId(''); setPendingLocation(null);
    setPendingBudgetMin(''); setPendingBudgetMax(''); setPendingCurrency('');
    setPendingMinDays(''); setPendingMaxDays('');
  }

  function resetFilters() {
    suggestions.clear();
    setPendingTitle('');
    selectionRevision.current += 1; setPendingDestinationKey('');
    setPendingLocationId('');
    setPendingLocation(null);
    setPendingBudgetMin('');
    setPendingBudgetMax('');
    setPendingCurrency('');
    setPendingMinDays('');
    setPendingMaxDays('');
    setPendingSort('LATEST');
    commitFilters(emptyFilters({ scope: appliedFilters.scope }));
  }

  function handleSelectDestination(node) {
    selectionRevision.current += 1;
    setPendingDestinationKey(node?.destinationKey || '');
    setPendingLocationId('');
    setPendingLocation(node);
  }

  function handleSelectLocation(node) {
    const id = snowflakePositiveId(node?.locationId);
    setPendingLocation(node ?? null);
    setPendingLocationId(id || '');
  }

  function clearLocation() {
    selectionRevision.current += 1; setPendingDestinationKey('');
    setPendingLocation(null);
    setPendingLocationId('');
  }

  /** 提交 keyword 搜尋：更新 applied q，保留其他 filters。 */
  function submitQuery(raw) {
    const q = String(raw ?? '').trim();
    suggestions.syncDraftQuery(q, { fetch: false });
    commitFilters({
      ...appliedFilters,
      q,
      locationId: pendingLocationId || appliedFilters.locationId,
      budgetMin: pendingBudgetMin,
      budgetMax: pendingBudgetMax,
      currency: pendingCurrency,
      minDays: pendingMinDays,
      maxDays: pendingMaxDays,
      sort: pendingSort,
    });
  }

  /**
   * GEO：套用 locationId filter，並清除 q，避免雙重條件。
   * JOURNEY：導向 public publication detail（publication id）。
   */
  function handleSelectSuggestion(item) {
    if (!item) return;
    if (item.type === 'JOURNEY') {
      const publicationId = snowflakePositiveId(item.id) || String(item.id);
      navigate(`/public/journeys/${publicationId}`);
      return;
    }
    if (item.type === 'GEO') {
      const locationId = snowflakePositiveId(item.id) || String(item.id);
      const node = {
        locationId,
        displayName: item.label,
        hasChildren: false,
      };
      setPendingLocation(node);
      setPendingLocationId(locationId);
      suggestions.syncDraftQuery('', { fetch: false });
      commitFilters({
        ...appliedFilters,
        q: '',
        titleQuery: pendingTitle.trim(),
        locationId,
        budgetMin: pendingBudgetMin,
        budgetMax: pendingBudgetMax,
        currency: pendingCurrency,
        minDays: pendingMinDays,
        maxDays: pendingMaxDays,
        sort: pendingSort,
      });
    }
  }

  /** Clear keyword：清 draft／applied q，保留其他 filters。 */
  function clearKeyword() {
    suggestions.clear();
    commitFilters({
      ...appliedFilters,
      q: '',
      locationId: pendingLocationId || appliedFilters.locationId,
      budgetMin: pendingBudgetMin,
      budgetMax: pendingBudgetMax,
      currency: pendingCurrency,
      minDays: pendingMinDays,
      maxDays: pendingMaxDays,
      sort: pendingSort,
    });
  }

  const BudgetContainer = dedicated ? 'details' : 'div';
  const appliedQ = appliedFilters.q;
  const hasAppliedFilters = ['q', 'titleQuery', 'locationId', 'destinationKey', 'minDays', 'maxDays', 'budgetMin', 'budgetMax'].some((key) => appliedFilters[key] !== '') || appliedFilters.sort !== 'LATEST';
  const sortLabel = (sort) => ['LIKES', 'COMMENTS', 'COPIES'].includes(sort) ? t(`travel.journeyDiscovery.sort${sort}`) : sort === 'OLDEST' ? t('travel.journeyDiscovery.oldest') : t(sort === 'HOT' ? (dedicated ? 'travel.journeyDiscovery.hot' : 'travel.discover.sort.hot') : 'travel.discover.sort.latest');

  return (
    <>
      {dedicated ? (
        <>
          <div className="journey-discovery__toolbar">
            <div className="journey-discovery__scopes" role="group" aria-label={t('travel.journeyDiscovery.scope')}>
              {['ALL', 'FOLLOWING'].map((scope) => (
                <button key={scope} type="button" aria-pressed={appliedFilters.scope === scope}
                  onClick={() => commitFilters({ ...appliedFilters, scope })}>
                  {t(scope === 'ALL' ? 'travel.journeyDiscovery.all' : 'travel.journeyDiscovery.following')}
                </button>
              ))}
            </div>
            <label className="journey-discovery__sort">{t('travel.discover.filters.sort')}
              <select value={appliedFilters.sort} onChange={(e) => applySort(e.target.value)}>
                {JOURNEY_DISCOVERY_SORTS.map((sort) => <option key={sort} value={sort}>{sortLabel(sort)}</option>)}
              </select>
            </label>
          </div>
          <button type="button" className="journey-discovery__refresh" disabled={phase === 'loading' || phase === 'loading-more'} onClick={() => load()}>{t('travel.journeyDiscovery.refresh')}</button>
          <form className="journey-discovery__search" onSubmit={(e) => { e.preventDefault(); applyFilters(); }}>
            <label>{t('travel.journeyDiscovery.name')}
              <input value={pendingTitle} maxLength={100} enterKeyHint="search" onChange={(e) => {
                setPendingTitle(e.target.value);
              }} placeholder={t('travel.journeyDiscovery.namePlaceholder')} />
            </label>
            <button className="journey-discovery__desktop-filter" type="button" onClick={() => setFiltersOpen(true)} aria-haspopup="dialog">
              {t('travel.discover.filters.location')}<br />{pendingLocation?.displayName || t('travel.discover.filters.allLocations')}
            </button>
            <button className="journey-discovery__desktop-filter" type="button" onClick={() => setFiltersOpen(true)} aria-haspopup="dialog">
              {t('travel.journeyDiscovery.days')}<br />{appliedFilters.minDays || '—'} – {appliedFilters.maxDays || '—'}
            </button>
            <button className="journey-discovery__mobile-filter" type="button" onClick={() => setFiltersOpen(true)} aria-haspopup="dialog">
              {t('travel.journeyDiscovery.filters')}
              {appliedFilters.locationId || appliedFilters.destinationKey || appliedFilters.minDays || appliedFilters.maxDays ? ' •' : ''}
            </button>
            <button type="submit">{t('travel.journeyDiscovery.search')}</button>
          </form>
          {appliedFilters.destinationKey ? <p className="journey-discovery__applied">{t('travel.journeyDiscovery.destination')}: {pendingLocation?.destinationKey === appliedFilters.destinationKey ? pendingLocation.displayName : t('travel.discover.filters.selectedLocation')}</p> : null}
          {appliedFilters.locationId ? <p className="journey-discovery__applied">{t('travel.discover.filters.location')}: {String(pendingLocation?.locationId) === String(appliedFilters.locationId) ? pendingLocation.displayName : t('travel.discover.filters.selectedLocation')}</p> : null}
          {appliedQ ? <p className="journey-discovery__applied">{tf('travel.discover.search.context', { q: appliedQ })} <button type="button" onClick={clearKeyword}>{t('travel.discover.search.clear')}</button></p> : null}
          {appliedFilters.titleQuery ? <p className="journey-discovery__applied">{t('travel.journeyDiscovery.name')}: {appliedFilters.titleQuery}</p> : null}
          {appliedFilters.minDays || appliedFilters.maxDays ? <p className="journey-discovery__applied">{t('travel.discover.filters.minDays')}: {appliedFilters.minDays || '—'} · {t('travel.discover.filters.maxDays')}: {appliedFilters.maxDays || '—'}</p> : null}
          {hasAppliedFilters ? <button className="journey-discovery__reset" type="button" onClick={resetFilters}>{t('travel.discover.filters.reset')}</button> : null}
        </>
      ) : null}
      <JourneyFilterDialog enabled={dedicated} open={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('travel.journeyDiscovery.filters')}>
      <section className="discover-filters" aria-label={t('travel.discover.filters.label') || 'Journey filters'}>
        {(!dedicated || appliedQ) ? <JourneySearchCombobox
          t={t}
          suggestions={suggestions}
          onSubmitQuery={submitQuery}
          onSelectSuggestion={handleSelectSuggestion}
          onClearKeyword={clearKeyword}
        /> : null}

        {appliedQ ? (
          <p className="discover-filters__context" data-testid="journey-search-context">
            {tf('travel.discover.search.context', { q: appliedQ })
              || `Searching for “${appliedQ}”`}
          </p>
        ) : null}

        <details className="discover-filters__location" open={dedicated || undefined}>
          <summary className="discover-filters__summary">
            {t('travel.discover.filters.location') || 'Location'}
            {pendingLocation ? `: ${pendingLocation.displayName}` : ''}
          </summary>
          {dedicated ? <JourneyDestinationPicker selected={pendingLocation} onSelect={handleSelectDestination} /> : <SocialLocationSelector
            searchable={false}
            selected={pendingLocation}
            onSelect={handleSelectLocation}
            rootLabel={t('travel.discover.filters.allLocations') || 'All locations'}
          />}
          {pendingLocation ? (
            <button type="button" className="discover-filters__clear-location" onClick={clearLocation}>
              {t('travel.discover.filters.clearLocation') || 'Clear location'}
            </button>
          ) : null}
        </details>

        {dedicated ? <div className="journey-discovery__day-presets" role="group" aria-label={t('travel.journeyDiscovery.days')}>
          {[['', ''], ['1', '3'], ['4', '7'], ['8', '14'], ['15', '']].map(([min, max]) => (
            <button type="button" key={`${min}:${max}`} aria-pressed={String(pendingMinDays) === min && String(pendingMaxDays) === max}
              onClick={() => { setPendingMinDays(min); setPendingMaxDays(max); }}>
              {min ? `${min}${max ? `–${max}` : '+'} ${t('travel.discover.days')}` : t('travel.journeyDiscovery.anyDays')}
            </button>
          ))}
          <button type="button" onClick={() => minDaysRef.current?.focus()}>{t('travel.journeyDiscovery.customDays')}</button>
        </div> : null}
        <div className="discover-filters__row">
          <label className="discover-filters__field">
            <span>{t('travel.discover.filters.minDays') || 'Min days'}</span>
            <input
              type="number"
              min="1"
              step="1"
              className="discover-filters__input discover-filters__input--short"
              ref={minDaysRef}
              value={pendingMinDays}
              onChange={(e) => setPendingMinDays(parsePositiveInt(e.target.value))}
              placeholder="1"
              aria-label={t('travel.discover.filters.minDays') || 'Minimum days'}
            />
          </label>
          <label className="discover-filters__field">
            <span>{t('travel.discover.filters.maxDays') || 'Max days'}</span>
            <input
              type="number"
              min="1"
              step="1"
              className="discover-filters__input discover-filters__input--short"
              value={pendingMaxDays}
              onChange={(e) => setPendingMaxDays(parsePositiveInt(e.target.value))}
              placeholder="—"
              aria-label={t('travel.discover.filters.maxDays') || 'Maximum days'}
            />
          </label>
        </div>

        <BudgetContainer className={dedicated ? 'journey-discovery__budget' : undefined}>
          {dedicated ? <summary>{t('travel.journeyDiscovery.budget')}</summary> : null}
        <div className="discover-filters__row">
          <label className="discover-filters__field">
            <span>{t('travel.discover.filters.budgetMin') || 'Budget min'}</span>
            <input
              type="number"
              min="0"
              step="any"
              className="discover-filters__input"
              value={pendingBudgetMin}
              onChange={(e) => setPendingBudgetMin(e.target.value)}
              placeholder="0"
              aria-label={t('travel.discover.filters.budgetMin') || 'Budget minimum'}
            />
          </label>
          <label className="discover-filters__field">
            <span>{t('travel.discover.filters.budgetMax') || 'Budget max'}</span>
            <input
              type="number"
              min="0"
              step="any"
              className="discover-filters__input"
              value={pendingBudgetMax}
              onChange={(e) => setPendingBudgetMax(e.target.value)}
              placeholder="—"
              aria-label={t('travel.discover.filters.budgetMax') || 'Budget maximum'}
            />
          </label>
          {(pendingBudgetMin !== '' || pendingBudgetMax !== '') ? (
            <label className="discover-filters__field">
              <span>{t('travel.discover.filters.currency') || 'Currency'}</span>
              <input
                type="text"
                className="discover-filters__input discover-filters__input--short"
                value={pendingCurrency}
                onChange={(e) => setPendingCurrency(e.target.value.toUpperCase())}
                placeholder="USDT"
                maxLength={8}
                aria-label={t('travel.discover.filters.currency') || 'Currency code'}
              />
            </label>
          ) : null}
        </div>

        </BudgetContainer>

        {!dedicated ? <div className="discover-filters__row discover-filters__sort-row">
          <span className="discover-filters__sort-label">{t('travel.discover.filters.sort') || 'Sort'}</span>
          <div className="discover-filters__sort-buttons" role="group" aria-label={t('travel.discover.filters.sort') || 'Sort'}>
            {JOURNEY_DISCOVERY_SORTS.map((s) => (
              <button
                key={s}
                type="button"
                className={`discover-filters__sort-btn${pendingSort === s ? ' discover-filters__sort-btn--active' : ''}`}
                aria-pressed={pendingSort === s}
                onClick={() => applySort(s)}
              >
                {sortLabel(s)}
              </button>
            ))}
          </div>
        </div> : null}
        <div className="discover-filters__actions">
          <button type="button" className="discover-filters__apply" onClick={applyFilters}>
            {t('travel.discover.filters.apply') || 'Apply'}
          </button>
          <button type="button" className="discover-filters__reset" onClick={dedicated ? clearPendingFilters : resetFilters}>
            {t('travel.discover.filters.reset') || 'Reset'}
          </button>
        </div>
      </section>
      </JourneyFilterDialog>

      {followingBlocked ? <ExploreGuestGate titleKey="travel.journeyDiscovery.signInTitle" bodyKey="travel.journeyDiscovery.signInBody" showBrowseForYou={false} /> : null}
      {!followingBlocked && (phase === 'loading' || phase === 'idle') ? (
        <section className="explore-state" aria-live="polite">
          <div className="explore-spinner" />
          <p>{t('travel.discover.loading') || 'Loading journeys…'}</p>
        </section>
      ) : null}

      {phase === 'error' && items.length === 0 ? (
        <section className="explore-state explore-state--error" role="alert">
          <h2>{t('travel.discover.error.title') || 'Could not load journeys'}</h2>
          <p>{error}</p>
          <button type="button" onClick={() => load()}>{t('travel.common.retry') || 'Try again'}</button>
        </section>
      ) : null}

      {!followingBlocked && phase === 'ready' && items.length === 0 && !hasMore ? (
        <section className="explore-state" data-testid="journeys-empty">
          <h2>{t(appliedFilters.scope === 'FOLLOWING' ? (hasFollowing === false ? 'travel.journeyDiscovery.noFollowing' : 'travel.journeyDiscovery.followingEmpty') : 'travel.discover.journeys.emptyTitle')}</h2>
          <p>
            {appliedFilters.scope === 'FOLLOWING' && hasFollowing === false
              ? t('travel.journeyDiscovery.noFollowingHint')
              : appliedQ
              ? (tf('travel.discover.journeys.emptyWithQuery', { q: appliedQ })
                || `No journeys matched “${appliedQ}”. Try another keyword or clear search.`)
              : (t('travel.discover.journeys.emptyHint') || 'Try adjusting your filters.')}
          </p>
          {appliedQ ? (
            <button type="button" onClick={clearKeyword}>
              {t('travel.discover.search.clear') || 'Clear'}
            </button>
          ) : null}
        </section>
      ) : null}

      {error && items.length > 0 ? (
        <div className="explore-inline-error" role="alert">
          {error}
          <button type="button" onClick={() => load({ append: true, cursor: nextCursor })}>
            {t('travel.common.retry') || 'Retry'}
          </button>
        </div>
      ) : null}

      <div className={dedicated ? "journey-discovery__cards" : "explore-cards"} data-testid="journeys-feed">
        {!followingBlocked && items.map((item) => (
          <JourneyDiscoveryCard key={item.publicationId} item={item} dedicated={dedicated} />
        ))}
      </div>

      {!followingBlocked && hasMore && nextCursor ? (
        <button
          className="explore-load-more"
          type="button"
          disabled={phase === 'loading-more'}
          onClick={() => load({ append: true, cursor: nextCursor })}
        >
          {phase === 'loading-more' ? (t('travel.common.loading') || 'Loading…') : (t('travel.common.loadMore') || 'Load more')}
        </button>
      ) : null}
      {items.length > 0 && !hasMore && phase === 'ready' ? (
        <p className="explore-end">{t('travel.discover.end') || "You're all caught up."}</p>
      ) : null}
    </>
  );
}
